import React from "react";
import styled from "styled-components";

import * as colors from "../../styles/colors";

const Figure = styled.figure`
  margin: 28px auto;
  padding: 0;
  max-width: 100%;
  width: 100%;
`;

const Container = styled.div`
  background: #0c0f14;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: stretch;

  @media (max-width: 820px) {
    flex-direction: column;
    padding: 16px;
    gap: 32px;
  }
`;

const Column = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0 50px;

  @media (max-width: 820px) {
    padding: 0;
  }
`;

const ColumnHeader = styled.div`
  text-align: center;
  margin-bottom: 6px;
`;

const ColumnTitle = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${(p) => p.$color || "#cbd5e1"};
  line-height: 1.2;
  margin-bottom: 3px;

  @media (max-width: 820px) {
    font-size: 20px;
  }
`;

const ColumnSubtitle = styled.div`
  font-size: 13px;
  font-style: italic;
  color: ${(p) => p.$color || "#94a3b8"};
  opacity: ${(p) => p.$opacity || 1};
`;

const Box = styled.div`
  background: ${(p) => p.$bg || "#1c2028"};
  border: 1px ${(p) => p.$borderStyle || "solid"}
    ${(p) => p.$border || "#3f4651"};
  border-radius: 8px;
  padding: 14px 16px;
  text-align: center;
`;

const BoxTitle = styled.div`
  font-size: ${(p) => p.$size || 15}px;
  font-weight: 600;
  color: #f1f5f9;
  line-height: 1.3;
  margin-bottom: 4px;
`;

const BoxBody = styled.div`
  font-size: 13px;
  color: ${(p) => p.$color || "#94a3b8"};
  line-height: 1.5;
`;

const PillRow = styled.div`
  display: flex;
  gap: 6px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: ${(p) => (p.$marginTop != null ? p.$marginTop : 8)}px;
`;

const Pill = styled.span`
  display: inline-block;
  padding: ${(p) => (p.$size === "lg" ? "6px 18px" : "3px 11px")};
  border-radius: 999px;
  font-size: ${(p) => (p.$size === "lg" ? "13px" : "10.5px")};
  font-weight: 700;
  letter-spacing: 0.5px;
  background: ${(p) => p.$bg || "transparent"};
  color: ${(p) => p.$color || "#fff"};
  border: ${(p) => (p.$border ? `1px solid ${p.$border}` : "none")};
  white-space: nowrap;
  line-height: 1.4;
`;

const SectionLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: ${(p) => p.$color || "#a5b4fc"};
  margin: ${(p) => p.$margin || "10px 0 4px"};
  text-align: center;
`;

const ItalicNote = styled.div`
  font-size: 12px;
  font-style: italic;
  color: #94a3b8;
  text-align: center;
  margin-top: 8px;
  line-height: 1.4;
`;

const BulletList = styled.div`
  text-align: center;

  > div {
    font-size: 13px;
    color: #cbd5e1;
    line-height: 1.55;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #3a3550;
  margin: 6px 14px !important;
`;

const ArrowDown = styled.div`
  width: 1.5px;
  height: 18px;
  background: ${(p) => p.$color || "#6b7280"};
  margin: 0 auto;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    left: -4px;
    bottom: -5px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid ${(p) => p.$color || "#6b7280"};
  }
`;

const Destination = styled.div`
  background: #1c1c2e;
  border: 1px solid #3a3550;
  border-radius: 8px;
  padding: 12px 14px;
  text-align: left;
`;

const DestinationTitle = styled.div`
  font-size: 14.5px;
  font-weight: 700;
  color: #f1f5f9;
  margin: 8px 0 3px;
  line-height: 1.3;
`;

const DestinationBody = styled.div`
  font-size: 12.5px;
  color: #94a3b8;
  line-height: 1.55;
`;

const FooterNote = styled.div`
  text-align: center;
  font-size: 11.5px;
  font-style: italic;
  color: #64748b;
  margin-top: 12px;
  line-height: 1.55;
`;

const Caption = styled.figcaption`
  margin-top: 12px;
  padding: 0 4px;
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.muted};
  text-align: center;
  font-style: italic;

  @media (max-width: 640px) {
    font-size: 13px;
    text-align: left;
    margin-top: 10px;
    padding: 0;
  }
`;

export function DspFlowDiagram({ caption }) {
  return (
    <Figure>
      <Container>
        {/* ============ OPENBCI ============ */}
        <Column>
          <ColumnHeader>
            <ColumnTitle $color="#cbd5e1">OpenBCI</ColumnTitle>
            <ColumnSubtitle $color="#94a3b8">
              DSP happens at the end of the chain
            </ColumnSubtitle>
          </ColumnHeader>

          {/* Headset + Cyton */}
          <Box>
            <BoxTitle>Ultracortex headset + Cyton board</BoxTitle>
            <BoxBody>
              Captures raw EEG. Cyton handles analog → digital.
              <br />
              No on-device processing.
            </BoxBody>
          </Box>
          <ArrowDown />

          {/* 2.4GHz radio */}
          <Box $bg="#2a1414" $border="#ef4444">
            <BoxTitle>2.4GHz proprietary radio</BoxTitle>
            <BoxBody $color="#cbd5e1">
              Streams raw signal to host
            </BoxBody>
            <PillRow>
              <Pill $bg="#ef4444" $color="#ffffff">
                UNENCRYPTED
              </Pill>
            </PillRow>
          </Box>
          <ArrowDown />

          {/* USB dongle */}
          <Box>
            <BoxTitle>USB dongle</BoxTitle>
            <BoxBody>Receives stream on a single host computer</BoxBody>
          </Box>
          <ArrowDown />

          {/* Client application */}
          <Box $bg="#2a1f0a" $border="#f59e0b" $borderStyle="dashed">
            <BoxTitle>Client application</BoxTitle>
            <PillRow>
              <Pill $bg="#f59e0b" $color="#1f1500">
                OpenBCI GUI or your custom application
              </Pill>
            </PillRow>
            <BulletList style={{ marginTop: 12 }}>
              <div style={{ color: "#fcd34d" }}>
                • Filters &amp; powerline notch
              </div>
              <div style={{ color: "#fcd34d" }}>
                • Feature extraction
              </div>
              <div style={{ color: "#fcd34d" }}>
                • Classification / scoring logic
              </div>
              <div style={{ color: "#fcd34d" }}>
                • Application layer &amp; UI
              </div>
            </BulletList>
          </Box>

          <FooterNote>
            OpenBCI Cyton config shown. Ganglion uses Bluetooth LE.
            <br />
            Both stream raw, unencrypted.
          </FooterNote>
        </Column>

        {/* ============ NEUROSITY CROWN ============ */}
        <Column>
          <ColumnHeader>
            <ColumnTitle $color="#a5b4fc">Neurosity Crown</ColumnTitle>
            <ColumnSubtitle $color="#a5b4fc" $opacity={0.75}>
              DSP happens at the source
            </ColumnSubtitle>
          </ColumnHeader>

          {/* Crown headset */}
          <Box $bg="#1f1c30" $border="#6366f1">
            <BoxTitle>Crown headset</BoxTitle>
            <BoxBody $color="#cbd5e1">
              8 channels @ 256Hz, dry electrodes
              <br />
              N3 chipset embedded inside
            </BoxBody>
          </Box>
          <ArrowDown $color="#818cf8" />

          {/* N3 chipset */}
          <Box $bg="#1e1b3a" $border="#6366f1">
            <BoxTitle $size={17}>N3 chipset (on-device OS)</BoxTitle>
            <PillRow>
              <Pill $bg="#10b981" $color="#062b1d">
                HARDWARE ENCRYPTED
              </Pill>
              <Pill $bg="#312e60" $color="#a5b4fc" $border="#818cf8">
                OVER-THE-AIR UPDATES
              </Pill>
            </PillRow>

            <SectionLabel>SIGNAL PROCESSING</SectionLabel>
            <BulletList>
              <div>• Filters &amp; signal cleaning</div>
              <div>• Auto powerline removal (50/60Hz)</div>
              <div>• Feature extraction</div>
            </BulletList>

            <Divider />

            <SectionLabel>COMPUTED METRICS</SectionLabel>
            <BulletList>
              <div>• Focus score · Calm score · Kinesis</div>
              <div>• Power-by-band, PSD, raw EEG</div>
            </BulletList>

            <ItalicNote>
              All computed before anything leaves the headset
            </ItalicNote>
          </Box>
          <ArrowDown $color="#818cf8" />

          {/* Multi-protocol output */}
          <Box $bg="#1f1c30" $border="#6366f1">
            <SectionLabel $margin="0 0 10px">
              SIMULTANEOUS OUTPUT TRANSPORTS
            </SectionLabel>
            <PillRow $marginTop={0}>
              <Pill
                $bg="#312e60"
                $color="#a5b4fc"
                $border="#818cf8"
                $size="lg"
              >
                WiFi
              </Pill>
              <Pill
                $bg="#312e60"
                $color="#a5b4fc"
                $border="#818cf8"
                $size="lg"
              >
                Bluetooth
              </Pill>
            </PillRow>
            <ItalicNote>No USB dongle needed</ItalicNote>
          </Box>
          <ArrowDown $color="#818cf8" />

          <SectionLabel $color="#94a3b8" $margin="2px 0 4px">
            REACHES ANY OF THESE, IN PARALLEL
          </SectionLabel>

          {/* Destinations */}
          <Destination>
            <Pill $bg="#1d2540" $color="#a5b4fc" $border="#6366f1">
              WiFi
            </Pill>
            <DestinationTitle>
              Web, cloud apps, and SDKs
            </DestinationTitle>
            <DestinationBody>
              Browser dashboards and hosted services. Streams over
              WebSockets.
            </DestinationBody>
          </Destination>

          <Destination>
            <Pill $bg="#1d2540" $color="#a5b4fc" $border="#6366f1">
              WiFi + Bluetooth
            </Pill>
            <DestinationTitle>
              Mobile app (iOS / Android)
            </DestinationTitle>
            <DestinationBody>
              Dual-connectivity. Bluetooth keeps the link alive when
              WiFi roams or drops.
            </DestinationBody>
          </Destination>

          <Destination>
            <Pill $bg="#10b981" $color="#062b1d">
              MCP
            </Pill>
            <DestinationTitle>
              AI tools (Claude, ChatGPT, agents)
            </DestinationTitle>
            <DestinationBody>
              Real-time cognitive state via Model Context Protocol.
              Rides on WiFi to any MCP-compatible AI client.
            </DestinationBody>
          </Destination>

          <Destination>
            <Pill $bg="#1d2540" $color="#a5b4fc" $border="#6366f1">
              LSL · OSC
            </Pill>
            <DestinationTitle>
              Computer (research / creative tools)
            </DestinationTitle>
            <DestinationBody>
              Streams direct from headset to any LSL or OSC client over
              WiFi. Research pipelines, creative coding, music tools.
            </DestinationBody>
          </Destination>
        </Column>
      </Container>
      {caption && <Caption>{caption}</Caption>}
    </Figure>
  );
}
